import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { fetchNumerosIgnorados, incluirNumeroIgnorado, removerNumeroIgnorado } from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { maskTelefone } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

/**
 * Com "+" na frente (o número colado do WhatsApp, ou estrangeiro), o texto fica
 * como veio — a máscara brasileira cortaria os dígitos do código do país e o
 * servidor reconhece o formato. Sem "+", a máscara (84) 98862-1330.
 */
function formatarEntrada(valor: string): string {
  const texto = valor.trimStart();
  return texto.startsWith('+') ? `+${texto.slice(1).replace(/[^\d\s()-]/g, '')}`.slice(0, 22) : maskTelefone(texto);
}

/**
 * Números que o Secretário nunca atende: colega, cartório, família. O resto ele
 * atende — inclusive quem já conversava com o advogado antes da configuração.
 */
export function NumerosIgnorados() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [telefone, setTelefone] = useState('');
  const [descricao, setDescricao] = useState('');
  const [erroCampo, setErroCampo] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.secretario.numerosIgnorados,
    queryFn: fetchNumerosIgnorados,
  });
  const invalidar = () => queryClient.invalidateQueries({ queryKey: queryKeys.secretario.numerosIgnorados });

  const incluir = useMutation({
    mutationFn: incluirNumeroIgnorado,
    onSuccess: () => {
      setTelefone('');
      setDescricao('');
      setErroCampo(null);
      invalidar();
      showSuccess('Número incluído.');
    },
    onError: (err) => {
      const classificado = classifyError(err);
      if (classificado.status === 422) {
        setErroCampo(classificado.message || 'Informe o número com DDD.');
        return;
      }
      notifyMutationError(err, showError);
    },
  });
  const remover = useMutation({
    mutationFn: removerNumeroIgnorado,
    onSuccess: () => {
      invalidar();
      showSuccess('O Secretário volta a atender esse número.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium">Números que o Secretário não atende</p>
        <p className="text-xs text-muted-foreground">
          Colega, cartório, família: o Secretário nunca responde a esses números. Os demais ele atende, inclusive quem
          já conversava com você antes.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      ) : isLoading ? (
        <Spinner size={16} label="Carregando números" />
      ) : data && data.length > 0 ? (
        <ul className="divide-y rounded-md border">
          {data.map((numero) => (
            <li key={numero.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium tabular-nums">{numero.telefone}</span>
                {numero.descricao && <span className="text-muted-foreground"> · {numero.descricao}</span>}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remover ${numero.telefone}`}
                onClick={() => remover.mutate(numero.id)}
                disabled={remover.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <form
        className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          incluir.mutate({ telefone, descricao: descricao.trim() || undefined });
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="numero-ignorado">Número</Label>
          <Input
            id="numero-ignorado"
            inputMode="tel"
            placeholder="(84) 98862-1330"
            value={telefone}
            onChange={(e) => {
              setTelefone(formatarEntrada(e.target.value));
              setErroCampo(null);
            }}
            aria-invalid={erroCampo ? true : undefined}
            aria-describedby={erroCampo ? 'numero-ignorado-erro' : undefined}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="numero-ignorado-descricao">Quem é (opcional)</Label>
          <Input
            id="numero-ignorado-descricao"
            placeholder="Cartório do 1º ofício"
            maxLength={120}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
        </div>
        <Button type="submit" variant="outline" disabled={!telefone || incluir.isPending}>
          {incluir.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
          Incluir
        </Button>
        {erroCampo && (
          <p id="numero-ignorado-erro" className="text-xs text-destructive sm:col-span-3">
            {erroCampo}
          </p>
        )}
      </form>
    </div>
  );
}
